// Casca do painel (TELAS §1) com o visual do design 10/2026 (DESIGN §4.5-4.7): barra lateral de 220 px com ícones, grupos
// separados só por um respiro (mesma ordem de sempre) e o marcador que desliza até a tela aberta; a marca NEXUS com o X
// aceso enquanto o tempo real está de pé (âmbar reconectando); rodapé com usuário e sair.
// NO CELULAR (até 820px, a régua do Atlas, 07/10/2026): a barra vira GAVETA. A faixa fina de cima saiu (design 10/2026):
// o ☰ mora no próprio cabeçalho de cada tela, com o pontinho de pendências.
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity,
  Brain,
  ChartColumn,
  ChevronLeft,
  Columns2,
  CreditCard,
  Film,
  House,
  Landmark,
  LayoutGrid,
  ListChecks,
  LogOut,
  Menu as IcoMenu,
  MessageCircle,
  Repeat,
  Settings,
  Smartphone,
  Sparkles,
  Table,
  TriangleAlert,
  Users,
  Workflow,
  ZoomIn,
  type LucideIcon,
} from 'lucide-react';
import { createContext, Suspense, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { api, EVENTO_SAIU } from './nucleo/api';
import { useTempoReal } from './nucleo/tempo-real';
import { Botao, BotaoIcone, Esqueleto, Menu, Placar, type ItemMenu } from './ui';

type Inicio = {
  pendentes: { comprovantes: number; precisa_voce: number; atencao: number; relatos: number; notas: number };
};
type Contador = keyof Inicio['pendentes'];

/** grupos = só um respiro entre eles, na mesma ordem de hoje (DESIGN §4.7). `forte` = esperando você (cor cheia). */
const MENU: { para: string; rotulo: string; ico: LucideIcon; grupo: number; fim?: boolean; contador?: Contador; forte?: boolean }[] = [
  { para: '/', rotulo: 'Início', ico: House, grupo: 1, fim: true },
  { para: '/fluxos', rotulo: 'Fluxos', ico: Workflow, grupo: 1 },
  { para: '/remarketing', rotulo: 'Remarketing', ico: Repeat, grupo: 1 },
  { para: '/ab', rotulo: 'Testes A/B', ico: Columns2, grupo: 1 },
  { para: '/dashboard', rotulo: 'Dashboard', ico: LayoutGrid, grupo: 2 },
  { para: '/planilha', rotulo: 'Planilha', ico: Table, grupo: 2 },
  { para: '/extrato', rotulo: 'Conferir extrato', ico: Landmark, grupo: 2 },
  { para: '/campanhas', rotulo: 'Campanhas', ico: ChartColumn, grupo: 3 },
  { para: '/criativos', rotulo: 'Criativos', ico: Film, grupo: 3 },
  { para: '/espionagem', rotulo: 'Espionagem', ico: ZoomIn, grupo: 3 },
  { para: '/crm', rotulo: 'CRM', ico: MessageCircle, grupo: 4, contador: 'precisa_voce', forte: true },
  { para: '/leads', rotulo: 'Leads', ico: Users, grupo: 4 },
  { para: '/solicitacoes', rotulo: 'Solicitações', ico: ListChecks, grupo: 4, contador: 'comprovantes', forte: true },
  { para: '/assinaturas', rotulo: 'Assinaturas', ico: CreditCard, grupo: 4 },
  { para: '/numeros', rotulo: 'Números', ico: Smartphone, grupo: 4 },
  { para: '/cerebros', rotulo: 'Cérebros', ico: Brain, grupo: 5 },
  { para: '/agente', rotulo: 'Agente', ico: Sparkles, grupo: 5, contador: 'notas' },
  { para: '/relatos', rotulo: 'Relatar erro', ico: TriangleAlert, grupo: 5, contador: 'relatos' },
  { para: '/saude', rotulo: 'Saúde', ico: Activity, grupo: 5 },
  { para: '/config', rotulo: 'Configurações', ico: Settings, grupo: 5 },
];

/** O ☰ do celular mora no cabeçalho de cada tela: ele abre a gaveta por aqui. */
const ContextoGaveta = createContext<{ abrir: () => void; pendentes: number }>({ abrir: () => {}, pendentes: 0 });
export function useMenuLateral(): { abrir: () => void; pendentes: number } {
  return useContext(ContextoGaveta);
}

/** A marca: NE·X·US. O X aceso é o sinal de "ao vivo" (sem animação: é estado). */
export function Marca({ vivo, estatica }: { vivo: boolean; estatica?: boolean }) {
  if (estatica)
    return (
      <span className="marca vivo" aria-label="NEXUS">
        NE<span className="x">X</span>US
      </span>
    );
  return (
    <span className={clsx('marca', vivo ? 'vivo' : 'caiu')} title={vivo ? 'ao vivo' : 'reconectando'} aria-label={`NEXUS, ${vivo ? 'ao vivo' : 'reconectando'}`}>
      NE<span className="x">X</span>US
    </span>
  );
}

export function Layout({ usuario }: { usuario: string | null }) {
  const { vivo } = useTempoReal();
  const { data } = useQuery({ queryKey: ['inicio'], queryFn: () => api.get<Inicio>('/inicio'), refetchInterval: 60_000 });

  const sair = async (): Promise<void> => {
    await api.post('/auth/sair');
    window.dispatchEvent(new Event(EVENTO_SAIU)); // o App troca pra tela de entrar (achado 39, 09/10/2026)
  };
  // a gaveta do celular fecha sozinha quando a tela muda (tocou num item do menu)
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setMenu(false), [pathname]);
  const pendentesTotal = (data?.pendentes?.precisa_voce ?? 0) + (data?.pendentes?.comprovantes ?? 0);
  // estável: a casca re-renderiza a cada evento do tempo real (o X aceso) e o cabeçalho de cada tela não precisa ir junto
  const gaveta = useMemo(() => ({ abrir: () => setMenu(true), pendentes: pendentesTotal }), [pendentesTotal]);

  // o marcador: um elemento só, que desliza até o item da tela aberta (a 1ª posição é sem animação)
  const lista = useRef<HTMLDivElement>(null);
  const marcador = useRef<HTMLSpanElement>(null);
  const [anda, setAnda] = useState(false);
  useLayoutEffect(() => {
    const m = marcador.current;
    const ativo = lista.current?.querySelector<HTMLElement>('.nav-i.ativo');
    if (!m) return;
    m.style.opacity = ativo ? '1' : '0';
    if (ativo) {
      m.style.transform = `translateY(${ativo.offsetTop}px)`;
      m.style.height = `${ativo.offsetHeight}px`;
    }
    if (!anda) requestAnimationFrame(() => setAnda(true));
  }, [pathname, menu, anda]);

  const inicial = (usuario ?? '?').trim().charAt(0).toUpperCase() || '?';
  const segmento = pathname.split('/')[1] ?? '';

  return (
    <ContextoGaveta.Provider value={gaveta}>
      <div className="flex h-full">
        <div
          onClick={() => setMenu(false)}
          className={clsx('veu z-40 hidden max-[820px]:block', menu ? 'opacity-100' : 'pointer-events-none opacity-0')}
          style={{ animation: 'none', transition: 'opacity 220ms var(--curva)', background: 'rgba(2,6,10,.55)' }}
        />
        <nav
          aria-label="menu"
          className={clsx(
            'flex w-[220px] shrink-0 flex-col border-r border-borda bg-surface',
            'max-[820px]:fixed max-[820px]:inset-y-0 max-[820px]:left-0 max-[820px]:z-50 max-[820px]:w-[248px] max-[820px]:max-w-[82vw] max-[820px]:shadow-n3 max-[820px]:transition-transform max-[820px]:duration-[220ms] max-[820px]:ease-curva',
            menu ? 'max-[820px]:translate-x-0' : 'max-[820px]:-translate-x-[101%]',
          )}
        >
          <div className="flex h-16 shrink-0 items-center px-[22px] pt-[env(safe-area-inset-top)]">
            <Marca vivo={vivo} />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3">
            <div ref={lista} className="relative">
              <span ref={marcador} className={clsx('marcador', anda && 'anda')} aria-hidden style={{ opacity: 0 }} />
              {MENU.map((m, i) => {
                const n = m.contador ? (data?.pendentes?.[m.contador] ?? 0) : 0;
                return (
                  <div key={m.para}>
                    {i > 0 && MENU[i - 1]!.grupo !== m.grupo && <div className="mx-1 my-1.5 h-px bg-borda" aria-hidden />}
                    <NavLink to={m.para} end={m.fim} className={({ isActive }) => clsx('nav-i', isActive && 'ativo')}>
                      <m.ico aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{m.rotulo}</span>
                      {n > 0 && (
                        <span className={clsx('contador', m.forte && 'forte')}>
                          <Placar valor={String(n)} chave={`nav.${m.para}`} />
                        </span>
                      )}
                    </NavLink>
                  </div>
                );
              })}
            </div>
          </div>
          <footer className="flex shrink-0 items-center gap-2.5 border-t border-borda py-3 pl-4 pr-2.5 pb-[calc(12px+env(safe-area-inset-bottom))]">
            <span className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-2 text-[13px] font-semibold text-texto" aria-hidden>
              {inicial}
              <span className={clsx('absolute -bottom-px -right-px size-2.5 rounded-full border-2 border-surface', vivo ? 'bg-ok' : 'bg-warn')} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-texto">{usuario ?? '—'}</span>
              <span className="block truncate text-[12px] text-muted">NEXUS do Arthur</span>
            </span>
            <BotaoIcone rotulo="sair" onClick={() => void sair()}>
              <LogOut />
            </BotaoIcone>
          </footer>
        </nav>
        <main className="min-w-0 flex-1">
          {/* a tela entra só com opacidade (160 ms); trocar de conversa no CRM não remonta (a chave é o 1º pedaço). Ela
              mesma é quem rola: o cabeçalho sticky fica preso no topo a tela inteira, e a rolagem volta ao topo na troca.
              Só na VERTICAL (20/10, Erik no iPhone: "está sambando pro lado"): com overflow-y auto o lado virava auto
              também, e qualquer pedaço mais largo que o celular arrastava a tela inteira, título junto. Quem é largo
              (tabela) rola dentro da própria caixa. */}
          <div key={segmento} className="tela-entra h-full overflow-y-auto overflow-x-hidden overscroll-contain">
            <Suspense
              fallback={
                <div className="p-6">
                  <Esqueleto linhas={5} />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </ContextoGaveta.Provider>
  );
}

/** A tela está no tamanho de celular (a mesma régua do CSS: até 820px). Pra decidir o que um clique faz, não o visual. */
export function useCelular(): boolean {
  const [celular, setCelular] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches);
  useEffect(() => {
    const m = window.matchMedia('(max-width: 820px)');
    const mudou = (): void => setCelular(m.matches);
    m.addEventListener('change', mudou);
    return () => m.removeEventListener('change', mudou);
  }, []);
  return celular;
}

export type AcaoPrimaria = { rotulo: string; aoClicar?: () => void; ico?: LucideIcon; carregando?: boolean; desligado?: boolean; menu?: ItemMenu[] };

/**
 * Cabeçalho da tela (DESIGN §4.5). PC, numa linha: título · contexto … linha ao vivo · segmentado · secundário · ⋯ ·
 * primário. Celular (≤ 820): ☰ (ou ‹) · título com o contexto embaixo · primário como "+" de 44 · ⋯; depois a linha
 * ao vivo e o segmentado em largura toda. `acoes` = o secundário (ou quem ainda não migrou).
 */
export function Cabecalho({
  titulo,
  contexto,
  subtitulo,
  aoVivo,
  segmentado,
  menu,
  primaria,
  secundaria,
  acoes,
  voltar,
  compacto,
  rotuloMenu,
  className,
}: {
  titulo: string;
  contexto?: ReactNode;
  /** o nome antigo do contexto */
  subtitulo?: ReactNode;
  aoVivo?: ReactNode;
  segmentado?: ReactNode;
  menu?: (ItemMenu | false | null | undefined)[];
  primaria?: AcaoPrimaria;
  /** o UM secundário (ex.: sincronizar): à vista no PC; no celular vira o 1º item do ⋯ */
  secundaria?: AcaoPrimaria;
  acoes?: ReactNode;
  voltar?: () => void;
  /** numa coluna estreita (a lista do CRM): título e ⋯ numa linha, a linha ao vivo embaixo, também no PC */
  compacto?: boolean;
  /** o nome do ⋯ pra leitor de tela (quando há mais de um na tela) */
  rotuloMenu?: string;
  className?: string;
}) {
  const { abrir, pendentes } = useMenuLateral();
  const ctx = contexto ?? subtitulo;
  const doMenu = (menu?.filter(Boolean) ?? []) as ItemMenu[];
  const itensMenu: ItemMenu[] = secundaria?.aoClicar
    ? [{ rotulo: secundaria.rotulo, ico: secundaria.ico, aoClicar: secundaria.aoClicar, soCelular: true }, ...doMenu]
    : doMenu;
  const Ico = primaria?.ico;
  return (
    <header className={clsx('cabecalho cab', compacto && 'cab-compacto', className)}>
      <span className="cab-esq">
        {voltar ? (
          <BotaoIcone rotulo="voltar" onClick={voltar}>
            <ChevronLeft />
          </BotaoIcone>
        ) : (
          <BotaoIcone rotulo="menu" onClick={abrir} className="relative">
            <IcoMenu />
            {pendentes > 0 && <span className="absolute right-2 top-2 size-2 rounded-full bg-danger" />}
          </BotaoIcone>
        )}
      </span>
      <div className="cab-tit">
        <h1>{titulo}</h1>
        {ctx && <p className="cab-ctx">{ctx}</p>}
      </div>
      {aoVivo && <div className="cab-vivo">{aoVivo}</div>}
      {segmentado && <div className="cab-seg">{segmentado}</div>}
      {secundaria && (
        <div className="cab-acoes max-[820px]:!hidden">
          <Botao variante="normal" ico={secundaria.ico} onClick={secundaria.aoClicar} carregando={secundaria.carregando} disabled={secundaria.desligado}>
            {secundaria.rotulo}
          </Botao>
        </div>
      )}
      {acoes && <div className="cab-acoes">{acoes}</div>}
      {itensMenu.length > 0 && (
        <div className={clsx('cab-menu', doMenu.length === 0 && 'min-[821px]:!hidden')}>
          <Menu itens={itensMenu} rotuloAcessivel={rotuloMenu} />
        </div>
      )}
      {primaria && (
        <div className="cab-prim">
          {primaria.menu ? (
            <Menu itens={primaria.menu} gatilho={{ conteudo: <span className="rot">{primaria.rotulo} ▾</span>, rotulo: primaria.rotulo, variante: 'primario', ico: Ico }} />
          ) : (
            <Botao variante="primario" ico={Ico} onClick={primaria.aoClicar} carregando={primaria.carregando} disabled={primaria.desligado} aria-label={primaria.rotulo}>
              <span className="rot">{primaria.rotulo}</span>
            </Botao>
          )}
        </div>
      )}
    </header>
  );
}

export function Conteudo({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={clsx('p-6 max-[820px]:p-3.5', className)}>{children}</div>;
}

export { Botao };
